import type { PxQuestion } from './types';

/**
 * Assign each answer a `uid` that is unique within its question (UCPWO-378).
 *
 * The PX Connect API does not guarantee unique answer `key`s within a question
 * (DEBEKA's `DBK_GE_5` ships `Debeka_ZE_5` twice). We never touch `key` (it must
 * be submitted back to the API verbatim); instead the reducer/renderer key off
 * this internal `uid`. The first occurrence of a key keeps `uid === key`; each
 * later duplicate gets a `#n` suffix (n = 2, 3, …), so the clean case stays
 * readable. Mutates in place and returns the same array.
 */
export const assignAnswerUids = (questions: PxQuestion[]): PxQuestion[] => {
    for (const question of questions) {
        const seen: Record<string, number> = {};
        for (const answer of question.answers) {
            const n = (seen[answer.key] = (seen[answer.key] ?? 0) + 1);
            answer.uid = n === 1 ? answer.key : `${answer.key}#${n}`;
        }
    }
    return questions;
};

/**
 * Map of question key → the answer keys that appear more than once in it. Empty
 * when the catalog is clean. Used for a dev-only warning at the fetch boundary
 * so dirty data coming from the real API is noticed, not silently handled.
 */
export const findDuplicateAnswerKeys = (questions: PxQuestion[]): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    for (const question of questions) {
        const counts: Record<string, number> = {};
        for (const answer of question.answers) {
            counts[answer.key] = (counts[answer.key] ?? 0) + 1;
        }
        const dups = Object.keys(counts).filter((key) => counts[key] > 1);
        if (dups.length) out[question.key] = dups;
    }
    return out;
};
